import { redirectToCodePage } from '@/api/mfaRequired';
import { refusalStateFromResponse } from '@/api/keyonFiles';
import { keyonFileUrl } from '@/utils/fileUrl';

/**
 * Playing and saving a video that keyon holds.
 *
 * A <video> element that fails says only "error": it cannot tell a container
 * this browser cannot read from a 409, 410 or 401 whose JSON body it was handed
 * instead of a video. probeKeyonVideo asks file.php the same question the
 * element asked, for one byte, so the answer can be mapped to a state. A HEAD
 * would not do: it has no body, so it cannot carry `mfa_required`. (A video
 * codec it lacks, iPhone HEVC on desktop Chrome or Firefox, often fires no
 * error at all and plays the audio alone: AttachmentVideoPlayer.vue catches
 * that by the picture's size, not here.)
 *
 * The fallback is a plain navigation to file.php?download=1, never the axios
 * blob path in keyonFiles.js: that buffers the whole file in a JS Blob, which
 * a phone cannot hold for a 100 MiB video, while a navigation lets the browser
 * write to disk as the bytes arrive and resume through Range.
 *
 * Both functions take injectable `{ fetch, doc, onMfaRequired, timeoutMs }`, as
 * keyonFiles.js does, so they can be tested without a browser or a router.
 */

// A probe that gets no answer must end: the card would otherwise wait for good.
const PROBE_TIMEOUT_MS = 15000;

function defaults(deps = {}) {
  return {
    fetch: deps.fetch || (typeof fetch === 'function' ? (...args) => fetch(...args) : null),
    doc: deps.doc || (typeof document !== 'undefined' ? document : null),
    onMfaRequired: deps.onMfaRequired || (() => redirectToCodePage()),
    timeoutMs: typeof deps.timeoutMs === 'number' ? deps.timeoutMs : PROBE_TIMEOUT_MS,
  };
}

async function refusalCode(res) {
  try {
    const body = await res.json();
    return body && typeof body.code === 'string' ? body.code : null;
  } catch (_) {
    return null;
  }
}

/**
 * The same request the media element makes for its first bytes: the playback
 * url (not download=1), the session cookie, one byte. No request marker: a GET
 * needs none, and the probe should hit file.php the way the element does.
 *
 * ok means keyon would serve the video, so a failure of the element is the
 * browser's (codec or container). Anything else is a state a page can show.
 * `mfa_required` has already sent the person to the code page, as a download's
 * does.
 * @returns {Promise<{ok:true}|{ok:false,state:'scan_pending'|'unavailable'|'mfa_required'|'rate_limited'|'error'}>}
 */
export async function probeKeyonVideo(uid, deps) {
  const url = keyonFileUrl(uid);
  if (!url) return { ok: false, state: 'unavailable' };
  const { fetch: send, onMfaRequired, timeoutMs } = defaults(deps);
  if (!send) return { ok: false, state: 'error' };

  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
  try {
    const res = await send(url, {
      method: 'GET',
      headers: { Range: 'bytes=0-0' },
      credentials: 'same-origin',
      ...(controller ? { signal: controller.signal } : {}),
    });
    if (!res || typeof res.status !== 'number') return { ok: false, state: 'error' };
    // A class keyon does not range answers the whole file with 200; either way
    // the one byte is all this asks for, so the rest is dropped unread.
    if (res.status === 200 || res.status === 206) {
      if (res.body && typeof res.body.cancel === 'function') {
        Promise.resolve(res.body.cancel()).catch(() => null);
      }
      return { ok: true };
    }
    const state = refusalStateFromResponse(res.status, await refusalCode(res));
    if (state === 'mfa_required') onMfaRequired();
    return { ok: false, state };
  } catch (_) {
    return { ok: false, state: 'error' };
  } finally {
    if (timer !== null) clearTimeout(timer);
  }
}

/**
 * Save a video. Probes first, so a 409 or 410 becomes a state on the card
 * instead of a JSON error page the browser would try to save; on ok it clicks
 * one anchor to file.php?download=1 and leaves the transfer to the browser.
 * The `download` attribute keeps even a late refusal from replacing the page.
 * Same result shape as downloadKeyonFile.
 * @returns {Promise<{ok:true}|{ok:false,state:'scan_pending'|'unavailable'|'mfa_required'|'rate_limited'|'error'}>}
 */
export async function startKeyonFileDownload(uid, filename, deps) {
  const downloadUrl = keyonFileUrl(uid, { download: true });
  if (!downloadUrl) return { ok: false, state: 'unavailable' };
  const probe = await probeKeyonVideo(uid, deps);
  if (!probe.ok) return probe;
  const { doc } = defaults(deps);
  if (!doc) return { ok: false, state: 'error' };
  const a = doc.createElement('a');
  a.href = downloadUrl;
  a.download = filename || 'download';
  a.rel = 'noopener';
  if (doc.body && typeof doc.body.appendChild === 'function') doc.body.appendChild(a);
  a.click();
  if (typeof a.remove === 'function') a.remove();
  return { ok: true };
}
